// TrendStudio.jsx
// Marketing → Trend Studio. One button: "What's hot this week?" → Claude
// with web search scans Reddit/TikTok hashtag pages/beauty news for what's
// going viral in med spa content right now, returns 8 ideas with scripts,
// hooks, hashtags, and image prompts. Owner can then click any trend and
// generate an AI image for the post/reel.

import { useEffect, useState } from "react";
import { supabase } from "./supabase";

const C = {
  bg: "#0a0a0f", panel: "#13131a", surface: "#1a1a22", border: "#27272f",
  text: "#f4f4f5", muted: "#a1a1aa", dim: "#71717a",
  gold: "#c9a84c", goldD: "#a08534", goldBg: "rgba(201,168,76,0.10)",
  goldBorder: "rgba(201,168,76,0.30)",
  green: "#2ecc87", greenL: "rgba(46,204,135,0.12)",
  red: "#ef4444", redL: "rgba(239,68,68,0.10)",
  blue: "#3b82f6", purple: "#a855f7", pink: "#ec4899",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

function platformBadge(p) {
  const lower = String(p || "").toLowerCase();
  if (lower.includes("tiktok"))   return { label: "TikTok",        color: "#000000", bg: "rgba(255,255,255,0.10)", border: "rgba(255,255,255,0.25)" };
  if (lower.includes("reel"))     return { label: "Instagram Reel", color: "#fff",   bg: "rgba(236,72,153,0.18)",  border: "rgba(236,72,153,0.40)" };
  if (lower.includes("instagram"))return { label: "Instagram Post", color: "#fff",   bg: "rgba(168,85,247,0.18)",  border: "rgba(168,85,247,0.40)" };
  return { label: p || "Social",    color: "#fff", bg: "rgba(168,85,247,0.18)", border: "rgba(168,85,247,0.40)" };
}

function ViralBar({ score }) {
  const v = Math.max(0, Math.min(100, +score || 0));
  const color = v >= 75 ? "#ef4444" : v >= 50 ? "#c9a84c" : "#71717a";
  return (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <div style={{flex:1,height:6,background:C.border,borderRadius:3,overflow:"hidden",position:"relative",minWidth:60}}>
        <div style={{width:v+"%",height:"100%",background:color,transition:"width 0.4s"}}/>
      </div>
      <div style={{fontSize:10,fontWeight:700,color,letterSpacing:"0.04em"}}>🔥 {v}</div>
    </div>
  );
}

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      style={{
        padding: "4px 10px", background: copied ? C.greenL : C.surface,
        color: copied ? C.green : C.muted, border: "1px solid "+(copied ? C.green+"50" : C.border),
        borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: F,
      }}
    >{copied ? "✓ Copied" : label}</button>
  );
}

export default function TrendStudio() {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [scan, setScan] = useState(null); // { scan_id, scanned_at, trends }
  const [imagesByTrend, setImagesByTrend] = useState({}); // { idx: [{url, format}] }
  const [generatingByTrend, setGeneratingByTrend] = useState({}); // { idx: bool }
  const [expandedIdx, setExpandedIdx] = useState(0);

  // Load the most recent scan on mount so opening the tab feels useful
  // even before clicking the big button.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("marketing_trend_scans")
        .select("id,scanned_at,trends")
        .order("scanned_at", { ascending: false })
        .limit(1);
      const last = data?.[0];
      if (last) setScan({ scan_id: last.id, scanned_at: last.scanned_at, trends: last.trends });
    })();
  }, []);

  // Load any images already generated for the loaded scan
  useEffect(() => {
    if (!scan?.scan_id) return;
    (async () => {
      const { data } = await supabase
        .from("marketing_generated_images")
        .select("trend_index,image_url,format,created_at")
        .eq("scan_id", scan.scan_id)
        .order("created_at", { ascending: false });
      const m = {};
      for (const r of data || []) {
        const i = r.trend_index ?? 0;
        if (!m[i]) m[i] = [];
        m[i].push({ url: r.image_url, format: r.format });
      }
      setImagesByTrend(m);
    })();
  }, [scan?.scan_id]);

  const runScan = async () => {
    setScanning(true);
    setError("");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error: e } = await supabase.functions.invoke("trend-scanner", {
        body: { user_id: user?.id || null },
      });
      if (e) throw e;
      if (data?.error) throw new Error(data.error);
      if (!data?.ok || !Array.isArray(data?.trends)) throw new Error("Bad response from scanner");
      setScan({ scan_id: data.scan_id, scanned_at: data.scanned_at, trends: data.trends });
      setImagesByTrend({});
      setExpandedIdx(0);
    } catch (e) {
      setError(e?.context?.error || e?.message || String(e));
    } finally {
      setScanning(false);
    }
  };

  const genImage = async (idx, format) => {
    const trend = scan?.trends?.[idx];
    if (!trend) return;
    setGeneratingByTrend(g => ({ ...g, [idx]: true }));
    setError("");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error: e } = await supabase.functions.invoke("gen-trend-image", {
        body: {
          prompt: trend.image_prompt,
          format,
          scan_id: scan.scan_id,
          trend_index: idx,
          trend_title: trend.title,
          user_id: user?.id || null,
        },
      });
      if (e) throw e;
      if (data?.error) throw new Error(data.error);
      if (!data?.image_url) throw new Error("No image returned");
      setImagesByTrend(prev => ({ ...prev, [idx]: [{ url: data.image_url, format }, ...(prev[idx] || [])] }));
    } catch (e) {
      setError(e?.context?.error || e?.message || String(e));
    } finally {
      setGeneratingByTrend(g => ({ ...g, [idx]: false }));
    }
  };

  return (
    <div style={{maxWidth:1200,margin:"0 auto"}}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Playfair+Display:wght@500;700;800&display=swap" rel="stylesheet"/>

      {/* Big primary action */}
      <div style={{
        background: "linear-gradient(135deg, rgba(201,168,76,0.10), rgba(236,72,153,0.05))",
        border: "1px solid "+C.goldBorder,
        borderRadius: 14,
        padding: "26px 28px",
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        marginBottom: 22,
      }}>
        <div style={{flex:"1 1 320px"}}>
          <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S,marginBottom:4}}>
            🔥 What's hot this week?
          </div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.55,maxWidth:560}}>
            Claude with web search scans TikTok hashtags, Instagram, Reddit beauty subreddits, and beauty news outlets to surface 8 trending content ideas — with hook, script, hashtags, and an image prompt ready to render.
          </div>
        </div>
        <button
          onClick={runScan}
          disabled={scanning}
          style={{
            padding:"14px 28px",
            background: scanning ? C.surface : "linear-gradient(135deg,"+C.gold+","+C.goldD+")",
            color: scanning ? C.muted : "#09090b",
            border: "none",
            borderRadius: 10,
            fontSize: 13, fontWeight: 800,
            cursor: scanning ? "wait" : "pointer",
            fontFamily: F, letterSpacing: "0.06em", textTransform: "uppercase",
            boxShadow: scanning ? "none" : "0 4px 18px rgba(201,168,76,0.4)",
            whiteSpace: "nowrap",
          }}
        >
          {scanning ? "🔍 Scanning..." : "🔍 Scan for trends"}
        </button>
      </div>

      {error && (
        <div style={{padding:"12px 16px",background:C.redL,border:"1px solid "+C.red+"40",borderRadius:10,color:C.red,fontSize:12,marginBottom:18}}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {scan && (
        <div style={{marginBottom:14,fontSize:11,color:C.muted}}>
          Showing scan from <strong style={{color:C.text}}>{new Date(scan.scanned_at).toLocaleString("en-US",{weekday:"short",month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</strong>
          {" · "}{scan.trends?.length || 0} ideas
        </div>
      )}

      {/* Trends list */}
      {scan?.trends?.map((trend, idx) => {
        const open = expandedIdx === idx;
        const platform = platformBadge(trend.platform);
        const trendImages = imagesByTrend[idx] || [];
        const isGenerating = !!generatingByTrend[idx];
        return (
          <div key={idx} style={{
            background: C.panel,
            border: "1px solid "+(open ? C.goldBorder : C.border),
            borderRadius: 12,
            marginBottom: 12,
            overflow: "hidden",
            transition: "border-color 0.2s",
          }}>
            {/* Collapsed header — always visible */}
            <div
              onClick={() => setExpandedIdx(open ? -1 : idx)}
              style={{padding:"14px 18px",display:"flex",alignItems:"center",gap:14,cursor:"pointer",flexWrap:"wrap"}}
            >
              <div style={{width:32,height:32,borderRadius:"50%",background:C.goldBg,border:"1px solid "+C.goldBorder,display:"flex",alignItems:"center",justifyContent:"center",color:C.gold,fontWeight:800,fontSize:13,flexShrink:0}}>
                {idx+1}
              </div>
              <div style={{flex:"1 1 240px",minWidth:0}}>
                <div style={{fontSize:14,fontWeight:800,color:C.text,fontFamily:S,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis"}}>
                  {trend.title || "Untitled trend"}
                </div>
                <div style={{fontSize:11,color:C.muted,lineHeight:1.45,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:1,WebkitBoxOrient:"vertical"}}>
                  {trend.why_trending || ""}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
                <span style={{fontSize:10,padding:"3px 9px",background:platform.bg,color:platform.color,border:"1px solid "+platform.border,borderRadius:50,fontWeight:700}}>
                  {platform.label}
                </span>
                <div style={{minWidth:90}}><ViralBar score={trend.viral_score}/></div>
                <div style={{fontSize:14,color:C.muted}}>{open ? "▴" : "▾"}</div>
              </div>
            </div>

            {/* Expanded detail */}
            {open && (
              <div style={{borderTop:"1px solid "+C.border,padding:"18px 22px",background:C.surface}}>
                {/* Hook */}
                {trend.hook && (
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:10,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.10em",marginBottom:6}}>🎯 Hook</div>
                    <div style={{padding:"10px 12px",background:C.panel,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,lineHeight:1.55,display:"flex",gap:10,alignItems:"flex-start",justifyContent:"space-between"}}>
                      <span style={{flex:1}}>{trend.hook}</span>
                      <CopyButton text={trend.hook}/>
                    </div>
                  </div>
                )}

                {/* Script */}
                {trend.script && (
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:10,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.10em",marginBottom:6,display:"flex",justifyContent:"space-between"}}>
                      <span>🎬 Script</span>
                      <CopyButton text={trend.script}/>
                    </div>
                    <div style={{padding:"12px 14px",background:C.panel,border:"1px solid "+C.border,borderRadius:7,fontSize:12.5,color:C.text,lineHeight:1.65,whiteSpace:"pre-wrap"}}>
                      {trend.script}
                    </div>
                  </div>
                )}

                {/* Caption + Hashtags */}
                <div style={{display:"grid",gridTemplateColumns:"1fr",gap:14,marginBottom:14}}>
                  {trend.caption && (
                    <div>
                      <div style={{fontSize:10,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.10em",marginBottom:6,display:"flex",justifyContent:"space-between"}}>
                        <span>✍️ Caption</span>
                        <CopyButton text={trend.caption + (Array.isArray(trend.hashtags) ? "\n\n" + trend.hashtags.map(h => h.startsWith("#") ? h : "#"+h).join(" ") : "")} label="Copy w/ tags"/>
                      </div>
                      <div style={{padding:"12px 14px",background:C.panel,border:"1px solid "+C.border,borderRadius:7,fontSize:12.5,color:C.text,lineHeight:1.6}}>
                        {trend.caption}
                      </div>
                    </div>
                  )}
                  {Array.isArray(trend.hashtags) && trend.hashtags.length > 0 && (
                    <div>
                      <div style={{fontSize:10,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.10em",marginBottom:6}}>🏷️ Hashtags</div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {trend.hashtags.map((h, i) => (
                          <span key={i} style={{fontSize:11,padding:"4px 9px",background:C.goldBg,color:C.gold,border:"1px solid "+C.goldBorder,borderRadius:6,fontWeight:600}}>{h.startsWith("#") ? h : "#"+h}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {trend.demographic && (
                  <div style={{marginBottom:14,padding:"10px 12px",background:C.panel,border:"1px solid "+C.border,borderRadius:7,fontSize:11.5,color:C.muted}}>
                    <strong style={{color:C.text}}>👥 Target:</strong> {trend.demographic}
                  </div>
                )}

                {/* Image generation */}
                <div style={{borderTop:"1px solid "+C.border,paddingTop:14,marginTop:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.10em",marginBottom:3}}>📸 AI Images</div>
                      <div style={{fontSize:10.5,color:C.muted}}>DALL-E 3 HD · Saved automatically</div>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <button
                        onClick={()=>genImage(idx, "post")}
                        disabled={isGenerating}
                        style={{padding:"7px 14px",background:isGenerating?C.surface:C.goldBg,color:isGenerating?C.muted:C.gold,border:"1px solid "+C.goldBorder,borderRadius:7,fontSize:11,fontWeight:700,cursor:isGenerating?"wait":"pointer",fontFamily:F}}
                      >
                        {isGenerating ? "Generating..." : "🖼️ Generate Post (1:1)"}
                      </button>
                      <button
                        onClick={()=>genImage(idx, "reel")}
                        disabled={isGenerating}
                        style={{padding:"7px 14px",background:isGenerating?C.surface:"rgba(236,72,153,0.10)",color:isGenerating?C.muted:C.pink,border:"1px solid rgba(236,72,153,0.30)",borderRadius:7,fontSize:11,fontWeight:700,cursor:isGenerating?"wait":"pointer",fontFamily:F}}
                      >
                        {isGenerating ? "Generating..." : "📱 Generate Reel (9:16)"}
                      </button>
                    </div>
                  </div>

                  {trend.image_prompt && (
                    <details style={{marginBottom:10}}>
                      <summary style={{fontSize:10,color:C.muted,cursor:"pointer",userSelect:"none"}}>Image prompt</summary>
                      <div style={{marginTop:6,padding:"10px 12px",background:C.panel,border:"1px solid "+C.border,borderRadius:7,fontSize:11,color:C.muted,fontFamily:"monospace",lineHeight:1.5}}>
                        {trend.image_prompt}
                      </div>
                    </details>
                  )}

                  {/* Gallery */}
                  {trendImages.length > 0 && (
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10,marginTop:10}}>
                      {trendImages.map((img, i) => (
                        <div key={i} style={{borderRadius:8,overflow:"hidden",border:"1px solid "+C.border,background:C.panel,position:"relative"}}>
                          <img src={img.url} alt="" style={{display:"block",width:"100%",height:"auto",aspectRatio:img.format==="reel"?"9/16":"1/1",objectFit:"cover"}}/>
                          <div style={{position:"absolute",top:6,right:6,padding:"2px 7px",background:"rgba(0,0,0,0.65)",color:"#fff",fontSize:9,fontWeight:700,borderRadius:4,letterSpacing:"0.05em"}}>
                            {img.format === "reel" ? "9:16" : "1:1"}
                          </div>
                          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"6px 8px",background:"linear-gradient(to top, rgba(0,0,0,0.85), transparent)",display:"flex",justifyContent:"space-between",gap:6}}>
                            <a href={img.url} download target="_blank" rel="noreferrer" style={{padding:"4px 10px",background:"rgba(255,255,255,0.15)",color:"#fff",borderRadius:5,fontSize:10,fontWeight:700,textDecoration:"none"}}>⬇ Download</a>
                            <button onClick={()=>{navigator.clipboard.writeText(img.url);}} style={{padding:"4px 10px",background:"rgba(255,255,255,0.15)",color:"#fff",border:"none",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer"}}>🔗 Copy URL</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!scan && !scanning && !error && (
        <div style={{padding:"60px 24px",textAlign:"center",background:C.panel,border:"1px dashed "+C.border,borderRadius:12,color:C.muted}}>
          <div style={{fontSize:42,opacity:0.4,marginBottom:12}}>🔥</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>No scans yet</div>
          <div style={{fontSize:11,maxWidth:380,margin:"0 auto",lineHeight:1.6}}>
            Click the gold button above to run your first trend scan. Claude will search across TikTok, Instagram, Reddit, and beauty media — it usually takes 30-60 seconds.
          </div>
        </div>
      )}
    </div>
  );
}
